import _ from 'lodash';
import { connect } from 'react-redux';
import React, { useEffect, useLayoutEffect, useMemo } from 'react';
/** import third-party libraries in the section above, keep the ascending order */

import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import makeStyles from '@mui/styles/makeStyles';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
/** import mui components in the section above, Sort Lines Ascending */

import { BlockLoadGroup } from 'widgets-v5/load-group';
import { SearchInput } from 'widgets-v6/standard-input';
import ClickToCopyButton from 'widgets/click-to-copy-button';
/** import widgets components in the section above, Sort Lines Ascending */

import {
    formatNumber_currency,
    formatNumber_percentage,
    formatNumber_whole,
} from 'utils/formatting';
/** import multi application imports in the section above, Sort Lines Ascending */

import { can } from 'services/can';
import { default as adminOrgFormActions } from 'packages/forms/admin-org-form/actions';
import { default as adminUserFormActions } from 'packages/forms/admin-users-form/actions';
import { default as deleteModalActions } from 'packages/containers/delete-user-modal/actions';
import { isInternalUser } from 'states/profile/business-rules';
import { PermissionsMapping } from 'common/constants/role-intents';
import { rolePresentationDictionary } from 'common/constants/role-presentation-dict';
import actions from './actions';
import AdminOrgForm from 'packages/forms/admin-org-form';
import AdminUserForm from 'packages/forms/admin-users-form';
import DeleteUserModal from 'packages/containers/delete-user-modal';
import flags from 'containers/flags/service';
import orgTypeMappings from 'common/constants/organizationTypeMappings';
import REVENUE_MODELS from 'common/constants/revenue-models';
import selector from './selector';
import TierCalculationStatus from 'packages/forms/admin-org-form/modules/fees/tier-calculation-status';
/** import single application imports in the section above, Sort Lines Ascending */

const dashboardStyles = makeStyles(theme => ({
    marginBottom: {
        marginBottom: theme.spacing(2),
    },
    sidebarContent: {
        padding: theme.spacing(1.5),
    },
    orgInfoPanelPaper: {
        overflow: 'hidden',
    },
    sidebarScroll: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
    },
    usersPanelPaper: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    panelContent: {
        padding: theme.spacing(1.5),
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        '& th:first-of-type, & td:first-of-type': {
            paddingLeft: 0,
        },
    },
    pageRoot: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
    },
    // Outer wrapper owns flex chain; descendant selectors constrain BlockLoadGroup + LoadGroup
    pageLoadGroup: {
        flex: 1,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        '& .ef4-block-load-group': {
            flex: 1,
            minHeight: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        },
        '& .ef4-load-group': {
            flex: 1,
            minHeight: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        },
    },
    name: {
        marginRight: theme.spacing(0.5),
    },
    sidebarSection: {
        marginBottom: theme.spacing(1.5),
    },
    gridRoot: {
        flex: 1,
        minHeight: 0,
        height: '100%',
    },
    gridItem: {
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    tableScroll: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
    },
    usersTable: {
        width: '100%',
        '& tbody .MuiTableRow-root:hover': {
            backgroundColor: '#F5F5F5',
        },
        '& th, & td': {
            wordBreak: 'break-word',
        },
        '& th:nth-child(3), & td:nth-child(3)': {
            whiteSpace: 'nowrap',
            wordBreak: 'normal',
        },
        '& th:last-child, & td:last-child': {
            width: 120,
            minWidth: 120,
            maxWidth: 120,
            whiteSpace: 'nowrap',
            wordBreak: 'normal',
            boxSizing: 'border-box',
        },
    },
    billingTiersTable: {
        '& > thead > tr > th': {
            border: 'none',
            verticalAlign: 'middle',
        },
        '& > tbody > tr > td': {
            border: 'none',
            verticalAlign: 'middle',
        },
    },
}));

/**
 * Lightweight debounce for search inputs — avoids lodash in this module.
 *
 * @param {(...args: unknown[]) => void} fn
 * @param {number} delayMs
 * @returns {((...args: unknown[]) => void) & { cancel: () => void }}
 */
function createDebounced(fn, delayMs) {
    let timeoutId = null;
    const debounced = (...args) => {
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            timeoutId = null;
            fn(...args);
        }, delayMs);
    };
    debounced.cancel = () => {
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }
    };
    return debounced;
}

const OwnOrganizationDashboard = ({
    dispatch,
    organization,
    users,
    currentUserId,
    isOrgLoading,
    isUsersLoading,
}) => {
    const classes = dashboardStyles();
    const useSpendingCycle = flags.isEnabled('efb_392_annually_spend_tiers');

    // Dispatch fetch before paint to avoid a flash of empty org labels and "No Users Found"
    useLayoutEffect(() => {
        dispatch(actions.fetchOwnOrganizationData());
    }, []);

    const onChangeTextFilterDebounced = useMemo(
        () => createDebounced(value => dispatch(actions.filterUsers(value)), 300),
        []
    );

    useEffect(() => {
        return () => {
            onChangeTextFilterDebounced.cancel();
        };
    }, [onChangeTextFilterDebounced]);

    const onChangeTextFilter = e => {
        const { value } = e.target;
        onChangeTextFilterDebounced(value);
    };

    const isPageLoadingUi = isOrgLoading || isUsersLoading;

    return (
        <div className={classes.pageRoot}>
            <div className={classes.pageLoadGroup}>
                <BlockLoadGroup size="large" isLoading={isPageLoadingUi}>
                    <Grid container spacing={1} className={classes.gridRoot}>
                        {/* admin_org__view — UI gate; urban ownOrganization GraphQL has no @authorize (product/tab gate). */}
                        {!isPageLoadingUi && can(PermissionsMapping.ADMIN_ORG__VIEW) && (
                            <Grid item xs={4} className={classes.gridItem}>
                                <Paper className={classes.orgInfoPanelPaper}>
                                    <div className={classes.sidebarScroll}>
                                        <div className={classes.sidebarContent}>
                                            <Grid
                                                container
                                                direction="row"
                                                alignItems="center"
                                                className={classes.sidebarSection}
                                            >
                                                <Typography variant="h6" className={classes.name}>
                                                    {organization.name}
                                                </Typography>
                                                {/* admin_org__edit — UI gate for own-org edit; urban PATCH /organizations/:id still uses subOrgUpdateMw (admin_subOrg__edit + OWNER). */}
                                                {can(PermissionsMapping.ADMIN_ORG__EDIT) && (
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Edit organization"
                                                        onClick={() =>
                                                            dispatch(
                                                                adminOrgFormActions.openForm(
                                                                    organization.id
                                                                )
                                                            )
                                                        }
                                                    >
                                                        <EditOutlinedIcon />
                                                    </IconButton>
                                                )}
                                            </Grid>
                                            <div className={classes.sidebarSection}>
                                                <Chip
                                                    label={_.startCase(
                                                        orgTypeMappings[organization.type]
                                                    )}
                                                    color="primary"
                                                    size="small"
                                                />
                                            </div>
                                            <Grid
                                                container
                                                direction="row"
                                                className={classes.sidebarSection}
                                                spacing={2}
                                            >
                                                <Grid item xs={6} xl={4}>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        Default Timezone
                                                    </Typography>
                                                    <Typography>
                                                        {organization.default_timezone}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6} xl={4}>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        Default Currency
                                                    </Typography>
                                                    <Typography>{organization.currency}</Typography>
                                                </Grid>
                                                <Grid item xs={6} xl={4}>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        Default Revenue Model
                                                    </Typography>
                                                    <Typography>
                                                        {REVENUE_MODELS[organization.revenueModel]}
                                                    </Typography>
                                                </Grid>
                                                {organization.type === 'media_buyer' &&
                                                    !useSpendingCycle && (
                                                        <Grid item xs={6} xl={3}>
                                                            <Typography
                                                                variant="body2"
                                                                color="textSecondary"
                                                            >
                                                                {'Current Platform Fee'}
                                                            </Typography>
                                                            <Typography>
                                                                {`${(
                                                                    organization.tech_fee * 100
                                                                ).toFixed(2)}%`}
                                                            </Typography>
                                                        </Grid>
                                                    )}
                                            </Grid>
                                            {organization.type === 'media_buyer' && (
                                                <Box mt={2}>
                                                    <Box mb={1}>
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                        >
                                                            {' '}
                                                            Private Deal Tiers{' '}
                                                        </Typography>
                                                        <Table
                                                            size="small"
                                                            className={classes.billingTiersTable}
                                                        >
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell>Min</TableCell>
                                                                    <TableCell>Max</TableCell>
                                                                    <TableCell>
                                                                        Platform Fee
                                                                    </TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {_.map(
                                                                    organization.privateInventorySpendTiers,
                                                                    tier => (
                                                                        <TableRow>
                                                                            <TableCell>
                                                                                {formatNumber_currency(
                                                                                    tier.min
                                                                                )}
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                {tier.max === -1
                                                                                    ? 'Infinity'
                                                                                    : formatNumber_currency(
                                                                                          tier.max
                                                                                      )}
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                {formatNumber_percentage(
                                                                                    tier.techFee
                                                                                )}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    )
                                                                )}
                                                            </TableBody>
                                                        </Table>
                                                    </Box>
                                                    <Box mb={1}>
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                        >
                                                            {' '}
                                                            Public Inventory Tiers{' '}
                                                        </Typography>
                                                        <Table
                                                            size="small"
                                                            className={classes.billingTiersTable}
                                                        >
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell>Min</TableCell>
                                                                    <TableCell>Max</TableCell>
                                                                    <TableCell>
                                                                        Platform Fee
                                                                    </TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {_.map(
                                                                    organization.publicInventorySpendTiers,
                                                                    tier => (
                                                                        <TableRow>
                                                                            <TableCell>
                                                                                {formatNumber_currency(
                                                                                    tier.min
                                                                                )}
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                {tier.max === -1
                                                                                    ? 'Infinity'
                                                                                    : formatNumber_currency(
                                                                                          tier.max
                                                                                      )}
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                {formatNumber_percentage(
                                                                                    tier.techFee
                                                                                )}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    )
                                                                )}
                                                            </TableBody>
                                                        </Table>
                                                    </Box>
                                                    {useSpendingCycle && (
                                                        <TierCalculationStatus
                                                            tierCalculation={
                                                                organization.latestTierCalculation
                                                            }
                                                            name={organization.name}
                                                            showTitle={false}
                                                            isInternalUser={isInternalUser()}
                                                        />
                                                    )}
                                                </Box>
                                            )}
                                        </div>
                                    </div>
                                </Paper>
                            </Grid>
                        )}
                        {/* admin_user__view — UI gate; urban ownOrganization.users has no admin_user__view @authorize (verifyInternalUserAndFilters only). */}
                        {!isPageLoadingUi && can(PermissionsMapping.ADMIN_USER__VIEW) && (
                            <Grid item xs={8} className={classes.gridItem}>
                                <Grid container className={classes.marginBottom}>
                                    <Grid item xs={6}>
                                        <SearchInput
                                            onChange={onChangeTextFilter}
                                            // User search supports name, id, and email for all users; the id hint in the
                                            // placeholder is internal-only (not relevant for non-internal users).
                                            // Keep isInternalUser() here in case visibility rules change.
                                            placeholder={
                                                isInternalUser()
                                                    ? 'Search User by Name, Email, or ID'
                                                    : 'Search User by Name or Email'
                                            }
                                        />
                                    </Grid>
                                </Grid>
                                <Paper className={classes.usersPanelPaper}>
                                    <div className={classes.panelContent}>
                                        <AppBar position="static" color="inherit" elevation={0}>
                                            <Toolbar
                                                disableGutters
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        flexWrap: 'wrap',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <Typography variant="h6" component="span">
                                                        {`${
                                                            organization.name
                                                        } (${formatNumber_whole(
                                                            users.length
                                                        )} ${
                                                            users.length === 1
                                                                ? 'user'
                                                                : 'users'
                                                        })`}
                                                    </Typography>
                                                    {isInternalUser() && (
                                                        <>
                                                            <Typography
                                                                variant="h6"
                                                                component="span"
                                                            >
                                                                {' · '}
                                                                {organization.id}
                                                            </Typography>
                                                            <ClickToCopyButton
                                                                variant="icon"
                                                                textToCopy={String(organization.id)}
                                                                textToDisplay="Copy organization ID"
                                                            />
                                                        </>
                                                    )}
                                                </Box>
                                                {/* admin_user__edit — urban POST /users (userEditMw); own-org user create. */}
                                                {can(PermissionsMapping.ADMIN_USER__EDIT) && (
                                                    <Button
                                                        color="primary"
                                                        variant="contained"
                                                        onClick={() =>
                                                            dispatch(
                                                                adminUserFormActions.openForm(
                                                                    null,
                                                                    organization.id
                                                                )
                                                            )
                                                        }
                                                    >
                                                        New User
                                                    </Button>
                                                )}
                                            </Toolbar>
                                        </AppBar>
                                        <div className={classes.tableScroll}>
                                            {!isPageLoadingUi && (
                                                <>
                                                    <Table
                                                        size="small"
                                                        stickyHeader
                                                        className={classes.usersTable}
                                                    >
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Name</TableCell>
                                                                <TableCell>Email</TableCell>
                                                                <TableCell>Role</TableCell>
                                                                <TableCell>Actions</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {users.map(user => (
                                                                <TableRow key={user.id}>
                                                                    <TableCell>
                                                                        {user.name}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {user.email}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {rolePresentationDictionary[
                                                                            user.global_role
                                                                        ] || 'Unknown'}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Box
                                                                            sx={{
                                                                                display: 'flex',
                                                                                justifyContent:
                                                                                    'flex-start',
                                                                            }}
                                                                        >
                                                                            {isInternalUser() && (
                                                                                <ClickToCopyButton
                                                                                    variant="icon"
                                                                                    textToCopy={String(
                                                                                        user.id
                                                                                    )}
                                                                                    textToDisplay="Copy user ID"
                                                                                />
                                                                            )}
                                                                            {/* admin_user__edit — urban PATCH /users/:id (userEditMw); own-org user edit. */}
                                                                            {can(
                                                                                PermissionsMapping.ADMIN_USER__EDIT
                                                                            ) && (
                                                                                <Tooltip
                                                                                    title={
                                                                                        <Typography variant="body1">
                                                                                            Edit
                                                                                            user
                                                                                        </Typography>
                                                                                    }
                                                                                >
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        aria-label="Edit user"
                                                                                        onClick={() =>
                                                                                            dispatch(
                                                                                                adminUserFormActions.openForm(
                                                                                                    user.id,
                                                                                                    organization.id
                                                                                                )
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        <EditOutlinedIcon />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            )}
                                                                            {/* admin_user__edit — urban DELETE /users/:id (deleteUserMw); own-org user delete. */}
                                                                            {can(
                                                                                PermissionsMapping.ADMIN_USER__EDIT
                                                                            ) && (
                                                                                <Tooltip
                                                                                    title={
                                                                                        <Typography variant="body1">
                                                                                            {user.id ===
                                                                                            currentUserId
                                                                                                ? 'You cannot delete your own account'
                                                                                                : 'Delete user'}
                                                                                        </Typography>
                                                                                    }
                                                                                >
                                                                                    <span>
                                                                                        <IconButton
                                                                                            size="small"
                                                                                            aria-label="Delete user"
                                                                                            disabled={
                                                                                                user.id ===
                                                                                                currentUserId
                                                                                            }
                                                                                            onClick={() =>
                                                                                                // Pass full user — same open(user) contract as child-org
                                                                                                dispatch(
                                                                                                    deleteModalActions.open(
                                                                                                        user
                                                                                                    )
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            <DeleteOutlineIcon />
                                                                                        </IconButton>
                                                                                    </span>
                                                                                </Tooltip>
                                                                            )}
                                                                        </Box>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                    {users.length === 0 && (
                                                        <Typography align="center" sx={{ pt: 2 }}>
                                                            No Users Found
                                                        </Typography>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </Paper>
                            </Grid>
                        )}
                    </Grid>
                </BlockLoadGroup>
            </div>
            <DeleteUserModal refreshUsers={() => dispatch(actions.fetchOwnOrganizationData())} />
            <AdminUserForm refreshUsers={() => dispatch(actions.fetchOwnOrganizationData())} />
            <AdminOrgForm
                refreshOrganization={() => dispatch(actions.fetchOwnOrganizationData())}
            />
        </div>
    );
};

export default connect(selector)(OwnOrganizationDashboard);
